import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type { Meta, Session, User } from '../lib/types';

interface AuthState {
  loading: boolean;
  user: User | null;
  isStaff: boolean;
  isAdmin: boolean;
  scopeProjectId: string | null;
  meta: Meta | null;
  setSession: (session: Session) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session>({ user: null });
  const [meta, setMeta] = useState<Meta | null>(null);

  const refresh = useCallback(async () => {
    try {
      // A dropped connection or a restarting server is not the same as being signed out: try again first.
      for (let attempt = 0; ; attempt += 1) {
        try {
          const [me, info] = await Promise.all([api.get<Session>('/api/auth/me'), api.get<Meta>('/api/meta')]);
          setSession(me);
          setMeta(info);
          return;
        } catch (error) {
          const transient = error instanceof ApiError && (error.status === 0 || error.status >= 500);
          if (!transient || attempt >= 3) throw error;
          await new Promise((resolve) => setTimeout(resolve, 800 * (attempt + 1)));
        }
      }
    } catch {
      setSession({ user: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onSignedOut = () => void refresh();
    window.addEventListener('mk:signed-out', onSignedOut);
    return () => window.removeEventListener('mk:signed-out', onSignedOut);
  }, [refresh]);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => undefined);
    setSession({ user: null });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      user: session.user,
      isStaff: !!session.isStaff,
      isAdmin: !!session.isAdmin,
      scopeProjectId: session.scopeProjectId ?? null,
      meta,
      setSession,
      refresh,
      logout,
    }),
    [loading, session, meta, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook lives with its provider
export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>.');
  return value;
}
